import React from "react";
import { theme } from "style/globalStyle";
import { every, find, isEmpty, isNil } from "lodash";

export const SecurityRatingConfigStatus = Object.freeze({
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
});

export const securityRatingDocumentCode = 'SECURITY_RATING_REPORT';

export const SecurityRatingControlScores = Object.freeze({
  ATTACK_SURFACE: "attack_surface",
  MAIL: "mail",
  WEB: "web",
  SECURITY_CONTROL: "security_control",
});

export const SecurityRatingPerformanceScores = Object.freeze({
  VULNERABILITY: "vulnerability",
  VULNERABILITY_SNAPSHOT: "vulnerability_snapshot",
  SECURITY_INCIDENT: "security_incident",
});

export const SecurityRatingScoresEnum = Object.freeze({
  ...SecurityRatingControlScores,
  ...SecurityRatingPerformanceScores,
})

export const SecurityRatingAllScoresEnum = Object.freeze({
  GLOBAL_SCORE: "global_score",
  ...SecurityRatingScoresEnum
})


export const SecurityRatingAllScoresPrefixedEnum = Object.freeze(Object.values(SecurityRatingAllScoresEnum).map(score => `security_rating_${score}`));

export const getPrefixedSecurityRatingScore = (score) => `security_rating_${score}`;
export const getUnPrefixedSecurityRatingScore = (prefixedScore) => prefixedScore?.substring(16);

export const SecurityRatingPerformanceEnum = Object.freeze({
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
});

// Estimated Graph representation of letter scores on a scale from 0 to 1000
export const SecurityRatingPerformanceValueForLetter = Object.freeze({
  A: 950,
  B: 750,
  C: 550,
  D: 350,
  E: 150,
  "-": 0
});

export const SecurityRatingLetterScoreEnum = Object.freeze({
  ...SecurityRatingPerformanceEnum,
  NOT_AVAILABLE: '-'
})

export const SecurityRatingPerformanceColor = Object.freeze({
  [SecurityRatingPerformanceEnum.A]: '#2B983E',
  [SecurityRatingPerformanceEnum.B]: theme.palette.primary.main,
  [SecurityRatingPerformanceEnum.C]: theme.palette.warning.warning400,
  [SecurityRatingPerformanceEnum.D]: theme.palette.warning.warning600,
  [SecurityRatingPerformanceEnum.E]: theme.palette.error.error400,
  '-': theme.palette.black.dark,
});

export const SecurityRatingGlobalScoreMaturityEnum = Object.freeze({
  BASIC: 'basic',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
});


export const SecurityRatingGlobalScorePerformanceColor = Object.freeze({
  ADVANCED: theme.palette.primary.main,
  INTERMEDIATE: theme.palette.warning.main,
  BASIC: theme.palette.error.error600
});

export const isSecurityRatingInProgress = (data) => {
  return isEmpty(data) || every(data, isNil);
};

export const isSecurityRatingAvailable = (data) => {
  return !every(data, isNil)
};

export const SecurityRatingIpProviders = Object.freeze({
  THIRDPARTY: 'THIRDPARTY',
  ACTIVITY: 'ACTIVITY',
  ALL: 'ALL',
});

export const SecurityRatingGlobalScorePerformanceOrder = Object.freeze({
  BASIC: 1,
  INTERMEDIATE: 2,
  ADVANCED: 3,
});

export const SecurityRatingPerformanceOrder = Object.freeze({
  [SecurityRatingPerformanceEnum.A]: 1,
  [SecurityRatingPerformanceEnum.B]: 2,
  [SecurityRatingPerformanceEnum.C]: 3,
  [SecurityRatingPerformanceEnum.D]: 4,
  [SecurityRatingPerformanceEnum.E]: 5,
  "-": 6,
});

export const getSecurityRatingGlobalScoreGraphColors = (maturity) => {
  switch (maturity) {
    case 'ADVANCED':
      return {
        bullet: {
          fill: '#D5ECD9',
          stroke: SecurityRatingGlobalScorePerformanceColor[maturity],
        },
        tooltip: {
          textColor: theme.palette.primary.primary300,
          fill: '#42523B',
        },
      }
    case 'INTERMEDIATE':
      return {
        bullet: {
          fill: '#FFECCF',
          stroke: SecurityRatingGlobalScorePerformanceColor[maturity],
        },
        tooltip: {
          textColor: theme.palette.warning.warning200,
          fill: theme.palette.warning.warning500,
        },
      };
    case 'BASIC':
    default:
      return {
        bullet: {
          fill: theme.palette.error.error200,
          stroke: SecurityRatingGlobalScorePerformanceColor[maturity],
        },
        tooltip: {
          textColor: theme.palette.error.error200,
          fill: '#872128',
        },
      };
  }
};

export const getSecurityRatingLetterScoreGraphColors = (letterScore) => {
  switch (letterScore) {
    case SecurityRatingPerformanceEnum.A:
      return {
        bullet: {
          fill: '#D5ECD9',
          stroke: SecurityRatingPerformanceColor[letterScore],
        },
        tooltip: {
          textColor: theme.palette.primary.primary300,
          fill: '#42523B',
        },
      };
    case SecurityRatingPerformanceEnum.B:
      return {
        bullet: {
          fill: '#D5ECD9',
          stroke: SecurityRatingPerformanceColor[letterScore],
        },
        tooltip: {
          textColor: theme.palette.primary.primary200,
          fill: '#518339',
        },
      };
    case SecurityRatingPerformanceEnum.C:
      return {
        bullet: {
          fill: '#FFECCF',
          stroke: SecurityRatingPerformanceColor[letterScore],
        },
        tooltip: {
          textColor: theme.palette.warning.warning200,
          fill: SecurityRatingPerformanceColor[letterScore],
        },
      };
    case SecurityRatingPerformanceEnum.D:
      return {
        bullet: {
          fill: '#FFECCF',
          stroke: SecurityRatingPerformanceColor[letterScore],
        },
        tooltip: {
          textColor: theme.palette.warning.warning200,
          fill: SecurityRatingPerformanceColor[letterScore],
        },
      };
    case SecurityRatingPerformanceEnum.E:
      return {
        bullet: {
          fill: theme.palette.error.error200,
          stroke: SecurityRatingPerformanceColor[letterScore],
        },
        tooltip: {
          textColor: theme.palette.error.error200,
          fill: theme.palette.error.error600,
        },
      };
    case '-':
    default:
      return {
        bullet: {
          fill: theme.palette.grey.grey200,
          stroke: SecurityRatingPerformanceColor[letterScore],
        },
        tooltip: {
          textColor: theme.palette.grey.grey200,
          fill: theme.palette.grey.grey800,
        },
      };
  }
};

export const extractSecurityRatingErrorCode = (errorMessage) => {
  const match = errorMessage?.match(/\(([^()]+?)::/);
  return match ? match[1] : null;
}

export const SecurityRatingContext = React.createContext({
  config: null,
  setConfig: () => { },
  onRiskChange: () => { },
});

export const splitSecurityRatingFiltersByScore = (filters) => {
  let securityRatingFilters = filters?.reduce((res, filter) => {
    const scoreLabel = filter.type?.substring(16)
    const scoreObj = find(res, { score: scoreLabel });
    // const value = findKey(EVAllScorePerfomance, (value) => value === filter.id);
    const value = filter.id;

    // Add a new score object if it doesnt exist.
    if (!scoreObj) {
      res.push({ score: scoreLabel, values: [value] })
    } else {
      // or add the value to its existing filter values.
      scoreObj.values.push(value)
    }
    return res;
  }, []);

  // Add remaining scores with empty values if they don't already exist
  const orderedFilters = [];
  Object.values(SecurityRatingAllScoresEnum).forEach((score) => {
    const scoreFilter = securityRatingFilters.find((filter) => filter.score === score);
    orderedFilters.push(scoreFilter || { score, values: [] });
  });

  return orderedFilters;
}